// The backlog: every feature, bug and improvement the team tracks. List and board views, saved views,
// filters (pick several values in each), grouping and sorting (owner included), multi-select with a bulk bar,
// right-click menus, pills that change in place, drag to rank or to change status, drop or paste a screenshot
// onto an item, and an Undo toast. Done items that haven't shipped are hidden until you turn on Show done
// (remembered in this browser); group headers still count them. Items nest Epic > story > sub-task: a story's
// sub-tasks open under it, epics sit in their own group with progress, and Group by Epic shows each epic's items.
// Firm requests arrive as items; New from firms lists the ones nobody has picked up yet.
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, CheckSquare, ChevronDown, ChevronRight, CornerDownRight, GripVertical, Inbox, Layers, ListFilter, MessageSquare, MoreHorizontal, Paperclip, Plus, Search, SlidersHorizontal, X, Zap } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { Avatar, Banner, Button, Card, Menu, MenuButton, Pill, ProgressBar, Spinner, cx, initialsOf, type MenuEntry } from '../../ui';
import { AREA_LABEL, AREAS } from '../feedback/types';
import { describe, fieldEntries, itemEntries, mergeEntries, type Field, type Handlers, type SetFields } from './actions';
import { NewItemDialog, type NewItemDefaults } from './NewItemDialog';
import {
  appendFiles, canSchedule, FILE_ACCEPT, IMAGE_TYPES, isOpen, isShipped, levelOf, itemFile, KIND_LABEL, KIND_TONE, KINDS, pastedImages, PRIORITIES, PRIORITY_LABEL,
  ORDER_KEY, PRIORITY_RANK, PRIORITY_TONE, STATUS_LABEL, STATUS_TONE, STATUSES, useAdminImage, whereLabel,
  SIZE_LABEL, type BacklogData, type Item, type ItemDetail, type ItemKind, type ItemStatus, type Priority, type Size,
} from './types';

type Updated = '' | 'week' | 'month' | 'stale';
/**
 * scope: 'open' (not shipped; Won't do and Done hidden unless picked or Show done is on), 'shipped' or 'all' (Archived).
 * Lists are "any of these"; empty = any. owner takes 'me' and 'none'; area takes 'none'. done is Show done.
 */
type Filters = {
  scope: 'open' | 'shipped' | 'all'; done: boolean; kind: ItemKind[]; status: ItemStatus[]; priority: Priority[]; release: string;
  owner: string[]; area: string[]; labels: string[]; size: Size[]; updated: Updated; epic: string[];
  following: boolean; steps: boolean; shots: boolean; firms: boolean; triage: boolean; q: string;
};
type ListKey = 'kind' | 'status' | 'priority' | 'owner' | 'area' | 'labels' | 'size' | 'epic';
type Group = 'release' | 'epic' | 'status' | 'owner' | 'priority' | 'area' | 'none';
type Sort = 'rank' | 'priority' | 'owner' | 'status' | 'updated' | 'created';
type View = { name: string; filters: Partial<Filters>; group?: Group; archived?: boolean };

const BLANK: Filters = {
  scope: 'open', done: false, kind: [], status: [], priority: [], release: '', owner: [], area: [], labels: [], size: [], updated: '', epic: [],
  following: false, steps: false, shots: false, firms: false, triage: false, q: '',
};
const BUILT_IN: View[] = [
  { name: 'All open', filters: {} },
  { name: 'Mine', filters: { owner: ['me'] } },
  { name: 'New from firms', filters: { triage: true } },
  { name: 'Following', filters: { following: true } },
  { name: 'Epics', filters: { kind: ['epic'] } },
  { name: 'High-priority bugs', filters: { kind: ['bug_fix'], priority: ['urgent', 'high'] } },
  { name: 'Asked for by firms', filters: { firms: true } },
  { name: 'Not scheduled', filters: { release: 'none' } },
  { name: 'Next hotfix', filters: { release: 'hotfix' } },
  { name: 'Done, not shipped', filters: { status: ['done'] } },
  { name: 'Stale (no update in 30 days)', filters: { updated: 'stale' } },
  { name: 'Shipped', filters: { scope: 'shipped' } },
  { name: 'Archived', filters: { scope: 'all' }, archived: true },
];
const VIEWS_KEY = 'upm.backlog.views';
const DONE_KEY = 'upm.backlog.show-done';
const UPDATED_LABEL: Record<Exclude<Updated, ''>, string> = { week: 'In the last 7 days', month: 'In the last 30 days', stale: 'Not in 30 days or more' };
const DAY = 86_400_000;
const SORT_LABEL: Record<Sort, string> = { rank: 'Rank', priority: 'Priority', owner: 'Owner A–Z', status: 'Status', updated: 'Last updated', created: 'Newest' };
const GROUP_LABEL: Record<Group, string> = { release: 'Release', epic: 'Epic', status: 'Status', owner: 'Owner', priority: 'Priority', area: 'Area', none: 'None' };
const BOARD_COLUMNS: ItemStatus[] = ['idea', 'todo', 'in_progress', 'done'];

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);
const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : typeof v === 'string' && v ? [v] : []);
/** A saved view from before the filters took several values: kind 'bug_fix' → ['bug_fix'], status 'open' → scope, priority 'high' → high or urgent. */
function upgrade(f: Record<string, unknown>): Partial<Filters> {
  const out = { ...f } as Partial<Filters> & Record<string, unknown>;
  for (const k of ['kind', 'owner', 'area', 'labels', 'size', 'epic'] as const) if (k in f) out[k] = list(f[k]) as never;
  if (typeof f.status === 'string') {
    if (f.status === 'shipped' || f.status === 'all' || f.status === 'open') { out.scope = f.status; out.status = []; }
    else out.status = [f.status as ItemStatus];
  }
  if (typeof f.priority === 'string') out.priority = f.priority === 'high' ? ['urgent', 'high'] : list(f.priority) as Priority[];
  delete out.done;
  return out;
}
const readViews = (): View[] => {
  try { return (JSON.parse(localStorage.getItem(VIEWS_KEY) ?? '[]') as View[]).map((v) => ({ ...v, filters: upgrade(v.filters as Record<string, unknown>) })); } catch { return []; }
};
const readDone = () => { try { return localStorage.getItem(DONE_KEY) === '1'; } catch { return false; } };
const typing = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [data-menu]');

type Ctx = { me: string; boxId: string; following: ReadonlySet<string>; now: number; byId: ReadonlyMap<string, Item> };

/** The epic an item belongs to: itself, its parent, or its story's parent. An archived epic (not in the list) counts as none. */
function epicIdOf(i: Item, byId: ReadonlyMap<string, Item>): string | null {
  if (i.kind === 'epic') return i.id;
  if (!i.parent_id || !i.parent) return null;
  const story = i.parent.kind === 'epic' ? null : byId.get(i.parent_id);
  const epicId = i.parent.kind === 'epic' ? i.parent_id : story?.parent?.kind === 'epic' ? story.parent_id : null;
  return epicId && byId.has(epicId) ? epicId : null;
}
/** Made from a firm request and not picked up yet: nobody owns it, it isn't scheduled, and nobody started it. */
const isNewFromFirm = (i: Item) => !!i.origin_request_id && !i.owner_id && !i.release_id && (i.status === 'idea' || i.status === 'todo');

function matches(i: Item, f: Filters, c: Ctx): boolean {
  if (f.scope === 'shipped' && !isShipped(i)) return false;
  if (f.scope === 'open') {
    if (!isOpen(i) && !(i.status === 'wont_do' && f.status.includes('wont_do') && !isShipped(i))) return false;
    if (i.status === 'done' && !f.done && !f.status.includes('done')) return false;
  }
  if (f.kind.length && !f.kind.includes(i.kind)) return false;
  if (f.status.length && !f.status.includes(i.status)) return false;
  if (f.priority.length && !f.priority.includes(i.priority)) return false;
  if (f.release === 'none' && i.release_id) return false;
  if (f.release === 'hotfix' && i.release_id !== c.boxId) return false;
  if (f.release && f.release !== 'none' && f.release !== 'hotfix' && i.release_id !== f.release) return false;
  if (f.owner.length && !f.owner.some((o) => (o === 'me' ? i.owner_id === c.me : o === 'none' ? !i.owner_id : i.owner_id === o))) return false;
  if (f.area.length && !f.area.includes(i.area ?? 'none')) return false;
  if (f.labels.length && !f.labels.some((l) => i.labels.includes(l))) return false;
  if (f.size.length && !(i.size && f.size.includes(i.size))) return false;
  if (f.updated) {
    const age = c.now - new Date(i.updated_at).getTime();
    if (f.updated === 'week' ? age > 7 * DAY : f.updated === 'month' ? age > 30 * DAY : age < 30 * DAY) return false;
  }
  if (f.epic.length && !f.epic.includes(epicIdOf(i, c.byId) ?? 'none')) return false;
  if (f.triage && !isNewFromFirm(i)) return false;
  if (f.following && !c.following.has(i.id)) return false;
  if (f.steps && i.checklist_done >= i.checklist_total) return false;
  if (f.shots && !i.attachment_count) return false;
  if (f.firms && !i.requests.length) return false;
  if (f.q) {
    const q = f.q.toLowerCase();
    const hay = `${i.reference} ${i.title} ${i.labels.join(' ')} ${i.requests.map((r) => `${r.reference} ${r.firm_name ?? ''}`).join(' ')}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

function comparator(sort: Sort): (a: Item, b: Item) => number {
  const rank = (a: Item, b: Item) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at);
  switch (sort) {
    case 'priority': return (a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || rank(a, b);
    case 'owner': return (a, b) => (a.owner_name ?? '￿').localeCompare(b.owner_name ?? '￿') || rank(a, b);
    case 'status': return (a, b) => STATUSES.indexOf(a.status) - STATUSES.indexOf(b.status) || rank(a, b);
    case 'updated': return (a, b) => b.updated_at.localeCompare(a.updated_at);
    case 'created': return (a, b) => b.created_at.localeCompare(a.created_at);
    default: return rank;
  }
}

/** hiddenDone: Done items in this group that Show done is hiding. */
type GroupOf = { key: string; label: ReactNode; hot?: boolean; items: Item[]; hiddenDone?: number; add?: Partial<NewItemDefaults> & { status?: ItemStatus; priority?: Priority } };

/** Release grouping puts epics, which aren't scheduled, in a group of their own. */
const groupKey = (i: Item, group: Group, byId: ReadonlyMap<string, Item>) => group === 'release' ? (i.kind === 'epic' ? 'epics' : i.release_id ?? 'none')
  : group === 'epic' ? (epicIdOf(i, byId) ?? 'none') : group === 'status' ? i.status : group === 'owner' ? (i.owner_id ?? 'none')
  : group === 'priority' ? i.priority : group === 'area' ? (i.area ?? 'none') : 'all';

function grouped(items: Item[], group: Group, data: BacklogData, byId: ReadonlyMap<string, Item>): GroupOf[] {
  if (group === 'none') return [{ key: 'all', label: null, items }];
  const map = new Map<string, Item[]>();
  const keyOf = (i: Item) => groupKey(i, group, byId);
  for (const i of items) map.set(keyOf(i), [...(map.get(keyOf(i)) ?? []), i]);
  if (group === 'epic') {
    const epics = data.items.filter((i) => i.kind === 'epic' && map.has(i.id)).sort((a, b) => a.sort_order - b.sort_order);
    return [...epics.map((e) => ({
      key: e.id, items: map.get(e.id)!, add: { parent_id: e.id, kind: 'task' as const },
      label: <><Layers aria-hidden size={13} className="inline" /> <Link to={`/admin/backlog/${e.id}`} className="text-inherit no-underline hover:underline">{e.reference} {e.title}</Link> · {e.children_done}/{e.children_total} done</>,
    })), ...(map.has('none') ? [{ key: 'none', items: map.get('none')!, label: 'No epic', add: {} }] : [])];
  }
  if (group === 'release') {
    const open = data.releases.filter((r) => r.kind === 'release');
    const order = [data.hotfix_box_id, ...open.map((r) => r.id), ...(map.has('epics') ? ['epics'] : []), 'none',
      ...[...map.keys()].filter((k) => k !== data.hotfix_box_id && k !== 'none' && k !== 'epics' && !open.some((r) => r.id === k))];
    return order.filter((k) => map.has(k) || k === data.hotfix_box_id || k === 'none' || open.some((r) => r.id === k)).map((k) => {
      const list = map.get(k) ?? [];
      const r = data.releases.find((x) => x.id === k);
      if (k === data.hotfix_box_id) return { key: k, hot: true, items: list, add: { release_id: k }, label: <><Zap aria-hidden size={13} className="inline" /> Next hotfix · goes live when rolled · announced with the next release</> };
      if (k === 'epics') return { key: k, items: list, add: { kind: 'epic' as const }, label: <><Layers aria-hidden size={13} className="inline" /> Epics · their items are scheduled one by one</> };
      if (k === 'none') return { key: k, items: list, add: { release_id: null }, label: 'Not scheduled' };
      const first = list[0];
      if (r) return { key: k, items: list, add: { release_id: k }, label: `${r.name}${r.target_date ? ` · target ${fmtDate(`${r.target_date}T12:00:00`)}` : ''}` };
      return { key: k, items: list, label: first ? whereLabel(first) : k };
    }).filter((g) => g.items.length || g.add);
  }
  if (group === 'status') return STATUSES.filter((s) => map.has(s)).map((s) => ({ key: s, items: map.get(s)!, label: STATUS_LABEL[s], add: { status: s } }));
  if (group === 'priority') return PRIORITIES.filter((p) => map.has(p)).map((p) => ({ key: p, items: map.get(p)!, label: PRIORITY_LABEL[p], add: { priority: p } }));
  if (group === 'owner') {
    return [...data.team.filter((p) => map.has(p.id)).map((p) => ({ key: p.id, items: map.get(p.id)!, label: p.name, add: { owner_id: p.id } })),
      ...(map.has('none') ? [{ key: 'none', items: map.get('none')!, label: 'Unassigned', add: { owner_id: null } }] : [])];
  }
  return [...AREAS.filter(([v]) => map.has(v)).map(([v, l]) => ({ key: v, items: map.get(v)!, label: l, add: { area: v } })),
    ...(map.has('none') ? [{ key: 'none', items: map.get('none')!, label: 'No area', add: { area: null } }] : [])];
}

export function BacklogPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view: 'list' | 'board' = params.get('view') === 'board' ? 'board' : 'list';
  const [data, setData] = useState<BacklogData | null>(null);
  const [archived, setArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(() => ({ ...BLANK, done: readDone() }));
  const [viewName, setViewName] = useState('All open');
  const [saved, setSaved] = useState<View[]>(readViews);
  const [group, setGroup] = useState<Group>('release');
  const [sort, setSort] = useState<Sort>('rank');
  const [lanes, setLanes] = useState<'none' | 'owner' | 'release' | 'priority'>('none');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; entries: MenuEntry[]; label: string } | null>(null);
  const [toast, setToast] = useState<{ text: string; undo?: () => void } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newItem, setNewItem] = useState<NewItemDefaults | null>(null);
  const filePick = useRef<HTMLInputElement>(null);
  const attachTo = useRef<string | null>(null);
  const toastTimer = useRef<number>();

  const load = useCallback(async (arch = archived) => {
    try { setData(await api.get<BacklogData>(`/api/admin/backlog${arch ? '?archived=1' : ''}`)); setError(null); }
    catch (e) { setError(msg(e, 'Could not load the backlog.')); }
  }, [archived]);
  useEffect(() => { void load(); }, [load]);

  const notify = useCallback((text: string, undo?: () => void) => {
    window.clearTimeout(toastTimer.current);
    setToast({ text, undo });
    toastTimer.current = window.setTimeout(() => setToast(null), 7000);
  }, []);

  const replace = useCallback((rows: Item[]) => setData((d) => {
    if (!d) return d;
    const byId = new Map(rows.map((r) => [r.id, r]));
    return { ...d, items: d.items.map((i) => byId.get(i.id) ?? i).filter((i) => archived ? !!i.archived_at : !i.archived_at) };
  }), [archived]);

  const handlers: Handlers = useMemo(() => ({
    update: (ids, set) => {
      if (!data) return;
      const before = data.items.filter((i) => ids.includes(i.id));
      const prev = before.map((i) => ({ id: i.id, set: Object.fromEntries(Object.keys(set).map((k) => [k, i[k as keyof Item]])) as SetFields }));
      const text = describe(before, set, data);
      const run = ids.length === 1
        ? api.patch<{ item: ItemDetail }>(`/api/admin/backlog/${ids[0]}`, set).then((r) => [r.item as Item])
        : api.post<{ items: Item[] }>('/api/admin/backlog/bulk', { ids, set }).then((r) => r.items);
      run.then((rows) => {
        replace(rows);
        const hides = set.status === 'done' && !filters.done && !filters.status.includes('done');
        notify(hides ? `${text}. Done items are hidden; turn on Show done to see them` : text, () => {
          void Promise.all(prev.map((p) => api.patch(`/api/admin/backlog/${p.id}`, p.set))).then(() => load(), (e) => setError(msg(e, 'Undo failed.')));
          setToast(null);
        });
      }, (e) => setError(msg(e, 'That change could not be saved.')));
    },
    archive: (ids, on) => {
      void api.post<{ items: Item[] }>('/api/admin/backlog/bulk', { ids, archive: on }).then(() => {
        setSelected(new Set());
        void load();
        notify(`${ids.length === 1 ? data?.items.find((i) => i.id === ids[0])?.reference ?? 'Item' : `${ids.length} items`} ${on ? 'archived' : 'restored'}`, () => {
          void api.post('/api/admin/backlog/bulk', { ids, archive: !on }).then(() => load());
          setToast(null);
        });
      }, (e) => setError(msg(e, 'Could not archive.')));
    },
    move: (id, to) => {
      setData((d) => {
        if (!d) return d;
        const ordered = [...d.items].sort(comparator('rank'));
        const it = ordered.find((i) => i.id === id);
        if (!it) return d;
        const rest = ordered.filter((i) => i.id !== id);
        const next = to === 'top' ? [it, ...rest] : [...rest, it];
        return { ...d, items: next.map((i, n) => ({ ...i, sort_order: n })) };
      });
      void api.post(`/api/admin/backlog/${id}/move`, { to }).catch((e) => { setError(msg(e, 'Could not move the item.')); void load(); });
    },
    merge: (ids, intoId) => {
      const into = data?.items.find((i) => i.id === intoId);
      if (!window.confirm(`Merge ${ids.length} item(s) into ${into?.reference}? Their firm requests, files, comments and checklists move over, and they are archived as duplicates.`)) return;
      void (async () => {
        try {
          for (const id of ids) await api.post(`/api/admin/backlog/${id}/merge`, { into_id: intoId });
          setSelected(new Set());
          notify(`Merged into ${into?.reference}`);
          void load();
        } catch (e) { setError(msg(e, 'Could not merge.')); void load(); }
      })();
    },
    open: (id, newTab) => { if (newTab) window.open(`/admin/backlog/${id}`, '_blank', 'noopener'); else navigate(`/admin/backlog/${id}`); },
    attach: (id) => { attachTo.current = id; filePick.current?.click(); },
    copy: (text, what) => { void navigator.clipboard.writeText(text).then(() => notify(`${what} copied`), () => setError('Copy failed.')); },
  }), [data, replace, notify, load, navigate, filters.done, filters.status]);

  const upload = useCallback(async (id: string, files: File[]) => {
    if (!files.length) return;
    const form = new FormData();
    appendFiles(form, files.map((file) => ({ file })));
    try {
      const res = await api.post<{ item: ItemDetail }>(`/api/admin/backlog/${id}/attachments`, form);
      replace([res.item]);
      notify(`${files.length === 1 ? (IMAGE_TYPES.includes(files[0]!.type) ? 'Screenshot' : 'File') : `${files.length} files`} added to ${res.item.reference}`);
    } catch (e) { setError(msg(e, 'The file could not be added.')); }
  }, [replace, notify]);

  const ctx = useMemo<Ctx | null>(() => (data ? {
    me: data.me, boxId: data.hotfix_box_id, following: new Set(data.following ?? []), now: Date.now(), byId: new Map(data.items.map((i) => [i.id, i])),
  } : null), [data]);
  const visible = useMemo(() => {
    if (!data || !ctx) return [];
    return data.items.filter((i) => matches(i, filters, ctx)).sort(comparator(sort));
  }, [data, ctx, filters, sort]);
  // A sub-task opens under its story when the story is shown too; otherwise it is a row of its own.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { top, childrenOf } = useMemo(() => {
    const ids = new Set(visible.map((i) => i.id));
    const kids = new Map<string, Item[]>();
    for (const i of visible) if (i.parent_id && levelOf(i) === 'subtask' && ids.has(i.parent_id)) kids.set(i.parent_id, [...(kids.get(i.parent_id) ?? []), i]);
    const nested = new Set([...kids.values()].flat().map((i) => i.id));
    return { top: visible.filter((i) => !nested.has(i.id) && !(group === 'epic' && i.kind === 'epic')), childrenOf: kids as ReadonlyMap<string, Item[]> };
  }, [visible, group]);
  /** Done items the current filters would show with Show done on. */
  const hiddenDone = useMemo(() => {
    if (!data || !ctx || filters.done || filters.scope !== 'open' || filters.status.includes('done')) return [];
    return data.items.filter((i) => i.status === 'done' && matches(i, { ...filters, done: true }, ctx));
  }, [data, ctx, filters]);
  const groups = useMemo(() => {
    if (!data) return [];
    return grouped(top, group, data, ctx!.byId).map((g) => ({ ...g, hiddenDone: hiddenDone.filter((i) => levelOf(i) !== 'subtask' && groupKey(i, group, ctx!.byId) === g.key).length }));
  }, [top, hiddenDone, group, data, ctx]);
  const flat = useMemo(() => groups.flatMap((g) => g.items.flatMap((i) => [i, ...(expanded.has(i.id) ? childrenOf.get(i.id) ?? [] : [])])), [groups, expanded, childrenOf]);
  useEffect(() => { try { sessionStorage.setItem(ORDER_KEY, JSON.stringify(flat.map((i) => i.id))); } catch { /* private window */ } }, [flat]);
  const targetsFor = useCallback((id: string) => {
    if (!data) return [];
    const ids = selected.has(id) ? [...selected] : [id];
    return data.items.filter((i) => ids.includes(i.id));
  }, [data, selected]);

  const openMenu = (x: number, y: number, entries: MenuEntry[], label: string) => setMenu({ x, y, entries, label });
  const contextFor = (e: ReactMouseEvent, item: Item) => {
    e.preventDefault();
    if (!data) return;
    setFocusId(item.id);
    openMenu(e.clientX, e.clientY, itemEntries(targetsFor(item.id), data, handlers), 'Item actions');
  };
  const fieldMenuAt = (el: HTMLElement, field: Field, item: Item) => {
    if (!data) return;
    const r = el.getBoundingClientRect();
    openMenu(r.left, r.bottom + 4, fieldEntries(field, targetsFor(item.id), data, handlers), field);
  };

  const toggle = (id: string, range: boolean) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (range && anchor) {
        const a = flat.findIndex((i) => i.id === anchor), b = flat.findIndex((i) => i.id === id);
        if (a >= 0 && b >= 0) { for (const i of flat.slice(Math.min(a, b), Math.max(a, b) + 1)) next.add(i.id); return next; }
      }
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setAnchor(id);
  };

  // Paste a screenshot onto the item last clicked.
  useEffect(() => {
    const paste = (e: ClipboardEvent) => {
      if (!focusId || typing(e.target)) return;
      const imgs = pastedImages(e);
      if (imgs.length) { e.preventDefault(); void upload(focusId, imgs); }
    };
    window.addEventListener('paste', paste);
    return () => window.removeEventListener('paste', paste);
  });

  const rename = async (item: Item, title: string) => {
    setRenaming(null);
    if (!title.trim() || title.trim() === item.title) return;
    try { replace([(await api.patch<{ item: ItemDetail }>(`/api/admin/backlog/${item.id}`, { title: title.trim() })).item]); notify(`${item.reference} renamed`); }
    catch (e) { setError(msg(e, 'Could not rename.')); }
  };
  const quickAdd = async (title: string, base: GroupOf['add']) => {
    const bug = /^bug:\s*/i.test(title);
    const body = { kind: bug ? 'bug_fix' : 'feature', title: title.replace(/^(bug|feature|improvement):\s*/i, '').trim(), ...base,
      ...(/^improvement:/i.test(title) ? { kind: 'improvement' } : {}) };
    if (!body.title) return;
    try { const res = await api.post<{ item: ItemDetail }>('/api/admin/backlog', body); setData((d) => (d ? { ...d, items: [...d.items, res.item] } : d)); notify(`${res.item.reference} added`); }
    catch (e) { setError(msg(e, 'Could not add the item.')); }
  };
  const applyView = (v: View) => {
    setViewName(v.name);
    setFilters((f) => ({ ...BLANK, ...v.filters, done: f.done, q: f.q }));
    if (v.group) setGroup(v.group);
    if (!!v.archived !== archived) { setArchived(!!v.archived); setData(null); setSelected(new Set()); }
  };
  const saveView = () => {
    const name = window.prompt('Name this view', '')?.trim();
    if (!name) return;
    const { done: _done, q: _q, ...kept } = filters;
    const next = [...saved.filter((v) => v.name !== name), { name, filters: kept, group }];
    setSaved(next);
    try { localStorage.setItem(VIEWS_KEY, JSON.stringify(next)); } catch { /* private window */ }
    setViewName(name);
  };
  const removeView = (name: string) => {
    const next = saved.filter((v) => v.name !== name);
    setSaved(next);
    try { localStorage.setItem(VIEWS_KEY, JSON.stringify(next)); } catch { /* private window */ }
  };
  const setF = <K extends keyof Filters>(k: K, v: Filters[K]) => { setFilters((f) => ({ ...f, [k]: v })); if (k !== 'q') setViewName(''); };
  const toggleIn = <K extends ListKey>(k: K, v: Filters[K][number]) => {
    setFilters((f) => { const cur = f[k] as string[]; return { ...f, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] }; });
    setViewName('');
  };
  const showDone = (on: boolean) => {
    setFilters((f) => ({ ...f, done: on }));
    try { localStorage.setItem(DONE_KEY, on ? '1' : '0'); } catch { /* private window: this visit only */ }
  };
  const clearAll = () => { setFilters((f) => ({ ...BLANK, q: f.q, done: f.done })); setViewName('All open'); };

  if (!data || !ctx) return error ? <Banner tone="error" role="alert">{error}</Banner> : <Spinner label="Loading the backlog" />;
  const open = data.items.filter((i) => isOpen(i) && i.status !== 'done');
  const doneCount = data.items.filter((i) => isOpen(i) && i.status === 'done').length;
  const fromFirms = data.items.filter((i) => !i.archived_at && isNewFromFirm(i)).length;
  const sel = data.items.filter((i) => selected.has(i.id));
  const counts = {
    open: open.length, bugs: open.filter((i) => i.kind === 'bug_fix').length,
    highBugs: open.filter((i) => i.kind === 'bug_fix' && PRIORITY_RANK[i.priority] <= 1).length,
    unscheduled: open.filter((i) => !i.release_id).length, firms: open.filter((i) => i.requests.length).length,
  };
  const releaseName = (id: string) => data.releases.find((r) => r.id === id)?.name ?? 'Release';
  const ownerName = (o: string) => (o === 'me' ? 'Me' : o === 'none' ? 'Unassigned' : data.team.find((p) => p.id === o)?.name ?? 'Someone');
  const labelsInUse = [...new Set(data.items.flatMap((i) => i.labels))].sort((a, b) => a.localeCompare(b));
  // Several values in one list: each toggles, and the menu stays open. "Any" clears the list.
  const multi = <K extends ListKey>(k: K, opts: Array<[Filters[K][number], string]>): MenuEntry[] => [
    { label: 'Any', checked: !filters[k].length, keepOpen: true, onSelect: () => setF(k, [] as never) },
    { kind: 'separator' },
    ...opts.map(([v, l]) => ({ label: l, checked: (filters[k] as string[]).includes(v), keepOpen: true, onSelect: () => toggleIn(k, v) })),
  ];
  const named = <K extends ListKey>(k: K, name: (v: Filters[K][number]) => string) => (filters[k] as string[]).map((v) => name(v as never)).join(' or ');
  // Filters that differ from the default view, shown as removable chips under the toolbar.
  const active = [
    filters.scope !== 'open' && { label: filters.scope === 'shipped' ? 'Shipped' : 'Everything', clear: () => setF('scope', 'open') },
    filters.kind.length > 0 && { label: `Type: ${named('kind', (k) => KIND_LABEL[k])}`, clear: () => setF('kind', []) },
    filters.status.length > 0 && { label: `Status: ${named('status', (st) => STATUS_LABEL[st])}`, clear: () => setF('status', []) },
    filters.priority.length > 0 && { label: `Priority: ${named('priority', (pr) => PRIORITY_LABEL[pr])}`, clear: () => setF('priority', []) },
    filters.release && { label: `Ships in: ${filters.release === 'none' ? 'Not scheduled' : filters.release === 'hotfix' ? 'Next hotfix' : releaseName(filters.release)}`, clear: () => setF('release', '') },
    filters.owner.length > 0 && { label: `Owner: ${named('owner', ownerName)}`, clear: () => setF('owner', []) },
    filters.area.length > 0 && { label: `Area: ${named('area', (a) => (a === 'none' ? 'No area' : AREA_LABEL[a] ?? a))}`, clear: () => setF('area', []) },
    filters.labels.length > 0 && { label: `Label: ${named('labels', (l) => l)}`, clear: () => setF('labels', []) },
    filters.size.length > 0 && { label: `Size: ${named('size', (z) => SIZE_LABEL[z])}`, clear: () => setF('size', []) },
    filters.updated && { label: `Updated: ${UPDATED_LABEL[filters.updated].toLowerCase()}`, clear: () => setF('updated', '') },
    filters.epic.length > 0 && { label: `Epic: ${named('epic', (e) => (e === 'none' ? 'No epic' : data.epics?.find((x) => x.id === e)?.title ?? 'Epic'))}`, clear: () => setF('epic', []) },
    filters.triage && { label: 'New from firms', clear: () => setF('triage', false) },
    filters.following && { label: 'Following', clear: () => setF('following', false) },
    filters.steps && { label: 'Has open steps', clear: () => setF('steps', false) },
    filters.shots && { label: 'Has files', clear: () => setF('shots', false) },
    filters.firms && { label: 'Has firm feedback', clear: () => setF('firms', false) },
  ].filter(Boolean) as Array<{ label: string; clear: () => void }>;
  const filterEntries = (): MenuEntry[] => [
    { label: 'Type', items: multi('kind', KINDS.map((k) => [k, KIND_LABEL[k]])) },
    { label: 'Status', items: multi('status', STATUSES.map((st) => [st, STATUS_LABEL[st]])) },
    { label: 'Priority', items: multi('priority', PRIORITIES.map((pr) => [pr, PRIORITY_LABEL[pr]])) },
    { label: 'Ships in', items: ([['', 'Any'], ['hotfix', 'Next hotfix'], ...data.releases.filter((r) => r.kind === 'release').map((r) => [r.id, r.name] as [string, string]), ['none', 'Not scheduled']] as Array<[string, string]>)
      .map(([v, l]) => ({ label: l, checked: filters.release === v, onSelect: () => setF('release', v) })) },
    { label: 'Epic', items: multi('epic', [...(data.epics ?? []).map((e) => [e.id, `${e.reference} · ${e.title}`] as [string, string]), ['none', 'No epic']]) },
    { label: 'Owner', items: multi('owner', [['me', 'Me'], ...data.team.map((p) => [p.id, p.name] as [string, string]), ['none', 'Unassigned']]) },
    { label: 'Area', items: multi('area', [...AREAS.map(([v, l]) => [v, l] as [string, string]), ['none', 'No area']]) },
    { label: 'Label', items: labelsInUse.length ? multi('labels', labelsInUse.map((l) => [l, l])) : [{ label: 'No labels yet', disabled: true }] },
    { label: 'Size', items: multi('size', (['s', 'm', 'l'] as Size[]).map((z) => [z, SIZE_LABEL[z]])) },
    { label: 'Updated', items: ([['', 'Any time'], ...Object.entries(UPDATED_LABEL)] as Array<[Updated, string]>)
      .map(([v, l]) => ({ label: l, checked: filters.updated === v, onSelect: () => setF('updated', v) })) },
    { kind: 'separator' },
    { label: 'New from firms, not picked up', checked: filters.triage, keepOpen: true, onSelect: () => setF('triage', !filters.triage) },
    { label: 'Following', checked: filters.following, keepOpen: true, onSelect: () => setF('following', !filters.following) },
    { label: 'Has open checklist steps', checked: filters.steps, keepOpen: true, onSelect: () => setF('steps', !filters.steps) },
    { label: 'Has files', checked: filters.shots, keepOpen: true, onSelect: () => setF('shots', !filters.shots) },
    { label: 'Has firm feedback', checked: filters.firms, keepOpen: true, onSelect: () => setF('firms', !filters.firms) },
    ...(active.length ? [{ kind: 'separator' as const }, { label: 'Clear all filters', onSelect: clearAll }] : []),
  ];
  const displayEntries = (): MenuEntry[] => [
    ...(view === 'list' ? [
      { kind: 'header' as const, label: 'Group by' },
      ...(Object.keys(GROUP_LABEL) as Group[]).map((g) => ({ label: GROUP_LABEL[g], checked: group === g, onSelect: () => setGroup(g) })),
      { kind: 'separator' as const },
      { kind: 'header' as const, label: 'Sort by' },
      ...(Object.keys(SORT_LABEL) as Sort[]).map((x) => ({ label: SORT_LABEL[x], checked: sort === x, onSelect: () => setSort(x) })),
    ] : [
      { kind: 'header' as const, label: 'Rows' },
      ...(['none', 'owner', 'release', 'priority'] as const).map((g) => ({ label: g === 'none' ? 'None' : GROUP_LABEL[g], checked: lanes === g, onSelect: () => setLanes(g) })),
    ]),
  ];
  const PRIMARY = ['All open', 'Mine', 'New from firms', 'Next hotfix', 'Not scheduled'];
  const moreViews = [...BUILT_IN.filter((v) => !PRIMARY.includes(v.name)), ...saved];
  const viewCount = (v: View) => (v.archived ? null : data.items.filter((i) => matches(i, { ...BLANK, ...v.filters, done: filters.done }, ctx)).length);
  const moreEntries = (): MenuEntry[] => [
    ...moreViews.map((v) => ({ label: `${v.name}${viewCount(v) === null ? '' : ` · ${viewCount(v)}`}`, checked: viewName === v.name, onSelect: () => applyView(v) })),
    { kind: 'separator' },
    { label: 'Save current view…', onSelect: saveView },
    ...(saved.length ? [{ label: 'Delete a saved view', items: saved.map((v) => ({ label: v.name, danger: true, onSelect: () => removeView(v.name) })) }] : []),
  ];
  const inMore = moreViews.some((v) => v.name === viewName);
  const tab = (on: boolean) => cx('flex min-h-8 items-center gap-1.5 rounded-lg border-0 px-2.5 text-[13px]', on ? 'bg-brand-soft font-semibold text-brand' : 'bg-transparent font-medium text-ink-2 hover:text-ink');
  const toolBtn = 'flex min-h-8 items-center gap-1.5 rounded-lg border border-line-btn bg-white px-2.5 text-[13px] font-semibold text-ink hover:bg-panel';

  const allVisibleSelected = flat.length > 0 && flat.every((i) => selected.has(i.id));
  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h1 className="page-title">Backlog</h1>
          <p className="m-0 text-[13px] text-muted">
            {counts.open} open{doneCount > 0 && <> · {doneCount} done, not shipped</>}{fromFirms > 0 && <> · <span className="font-semibold text-brand">{fromFirms} new from firms</span></>} · <span className={counts.highBugs ? 'font-semibold text-bad' : ''}>{counts.bugs} bugs{counts.highBugs ? ` (${counts.highBugs} high)` : ''}</span> · {counts.unscheduled} not scheduled · {counts.firms} from firm feedback
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div role="group" aria-label="View" className="flex gap-0.5 rounded-[10px] bg-subtle p-[3px]">
            {(['list', 'board'] as const).map((v) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => setParams(v === 'board' ? { view: 'board' } : {})}
                className={cx('min-h-8 rounded-lg border-0 px-3 text-[13px]', view === v ? 'bg-white font-semibold text-ink shadow-seg' : 'bg-transparent font-medium text-ink-2')}>{v === 'list' ? 'List' : 'Board'}</button>
            ))}
          </div>
          <Button variant="primary" size="sm" onClick={() => setNewItem({})}><Plus aria-hidden size={16} /> New item</Button>
        </div>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}

      <Card className="overflow-visible p-0">
        <div className="flex flex-wrap items-center gap-1 border-b border-line-soft px-3 py-2">
          {BUILT_IN.filter((v) => PRIMARY.includes(v.name)).map((v) => (
            <button key={v.name} type="button" aria-pressed={viewName === v.name} onClick={() => applyView(v)} className={tab(viewName === v.name)}>
              {v.name === 'Next hotfix' && <Zap aria-hidden size={13} />}{v.name === 'New from firms' && <Inbox aria-hidden size={13} />}{v.name}<span className="text-xs font-semibold text-muted">{viewCount(v)}</span>
            </button>
          ))}
          <MenuButton label="More views" entries={moreEntries} className={tab(inMore)}>
            {inMore ? viewName : 'More'}<ChevronDown aria-hidden size={13} className="opacity-60" />
          </MenuButton>
          <span className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
            <label className="relative flex min-w-0 flex-1 items-center sm:flex-none">
              <Search aria-hidden size={14} className="pointer-events-none absolute left-2.5 text-muted" />
              <input type="search" aria-label="Search the backlog" placeholder="Search" value={filters.q} onChange={(e) => setF('q', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setF('q', ''); (e.target as HTMLInputElement).blur(); } }}
                className="h-8 w-full min-w-0 rounded-lg border border-line-btn bg-white pl-8 pr-2 text-[13px] sm:w-44" />
            </label>
            <MenuButton label="Filter" entries={filterEntries} className={cx(toolBtn, active.length > 0 && 'border-brand text-brand')}>
              <ListFilter aria-hidden size={14} /> Filter{active.length > 0 && <span className="rounded-pill bg-brand px-1.5 text-[11px] text-white">{active.length}</span>}
            </MenuButton>
            <button type="button" aria-pressed={filters.done} onClick={() => showDone(!filters.done)}
              title={filters.done ? 'Hide items that are Done but not shipped' : 'Show items that are Done but not shipped'}
              className={cx(toolBtn, filters.done && 'border-brand bg-brand-soft text-brand')}>
              <CheckSquare aria-hidden size={14} /> {filters.done ? 'Hide done' : 'Show done'}
              {!filters.done && hiddenDone.length > 0 && <span className="rounded-pill bg-subtle px-1.5 text-[11px] text-ink-2">{hiddenDone.length}</span>}
            </button>
            <MenuButton label="Display" entries={displayEntries} className={toolBtn}>
              <SlidersHorizontal aria-hidden size={14} /><span className="hidden sm:inline">{view === 'list' ? `${GROUP_LABEL[group]} · ${SORT_LABEL[sort]}` : 'Display'}</span>
            </MenuButton>
          </span>
        </div>
        {active.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-line-soft px-3 py-1.5">
            {active.map((a) => (
              <span key={a.label} className="inline-flex items-center gap-1 rounded-pill bg-brand-soft py-0.5 pl-2.5 pr-1 text-xs font-semibold text-brand">
                {a.label}<button type="button" aria-label={`Remove ${a.label}`} onClick={a.clear} className="flex h-5 w-5 items-center justify-center rounded-full border-0 bg-transparent p-0 text-brand hover:bg-white/70"><X aria-hidden size={11} /></button>
              </span>
            ))}
            <button type="button" onClick={clearAll} className="border-0 bg-transparent px-1 text-xs font-semibold text-muted hover:text-ink">Clear all</button>
          </div>
        )}

        {sel.length > 0 && (
          <div role="toolbar" aria-label="Bulk actions" className="sticky top-0 z-20 flex flex-wrap items-center gap-2 bg-brand px-3 py-2 text-[13px] text-white">
            <span className="font-bold">{sel.length} selected</span><span className="h-5 w-px bg-header-rule" />
            {(['status', 'priority', 'release', 'owner', 'epic'] as Field[]).map((f) => (
              <MenuButton key={f} label={f} entries={() => fieldEntries(f, sel, data, handlers)}
                className="flex min-h-[30px] items-center gap-1 rounded-[10px] border border-[#5A6C99] bg-transparent px-3 text-[13px] font-semibold text-white hover:bg-nav-active">
                {{ status: 'Status', priority: 'Priority', release: 'Ships in', owner: 'Owner', kind: 'Type', area: 'Area', epic: 'Epic' }[f]}<ChevronDown aria-hidden size={13} />
              </MenuButton>
            ))}
            <MenuButton label="More actions" entries={() => itemEntries(sel, data, handlers).filter((e) => !('label' in e) || !['Status', 'Priority', 'Ships in', 'Assign to'].includes(String(e.label)))}
              className="flex min-h-[30px] items-center gap-1 rounded-[10px] border border-[#5A6C99] bg-transparent px-2.5 text-[13px] font-semibold text-white hover:bg-nav-active"><MoreHorizontal aria-hidden size={15} /></MenuButton>
            <button type="button" onClick={() => setSelected(new Set())} className="ml-auto min-h-[30px] rounded-lg border-0 bg-transparent px-2 text-[13px] text-header-fg">Clear</button>
          </div>
        )}

        {view === 'list' ? (
          <ListView groups={active.length || filters.q ? groups.filter((g) => g.items.length) : groups} data={data} sort={sort} setSort={setSort} selected={selected} focusId={focusId} renaming={renaming}
            allSelected={allVisibleSelected} onSelectAll={() => setSelected(allVisibleSelected ? new Set() : new Set(flat.map((i) => i.id)))}
            onToggle={toggle} onFocus={setFocusId} onContext={contextFor} onField={fieldMenuAt} onRename={rename} onCancelRename={() => setRenaming(null)}
            onMore={(el, item) => { const r = el.getBoundingClientRect(); setFocusId(item.id); openMenu(r.right - 240, r.bottom + 4, itemEntries(targetsFor(item.id), data, handlers), 'Item actions'); }}
            onDropFiles={upload} onQuickAdd={quickAdd} canRank={sort === 'rank' && !archived} onShowDone={() => showDone(true)}
            childrenOf={childrenOf} expanded={expanded} onExpand={(id) => setExpanded((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; })}
            onEpic={(id) => { setF('epic', [id]); }}
            onReorder={(id, beforeId) => {
              setData((d) => {
                if (!d) return d;
                const ordered = [...d.items].sort(comparator('rank')).filter((i) => i.id !== id);
                const at = ordered.findIndex((i) => i.id === beforeId);
                const it = d.items.find((i) => i.id === id)!;
                ordered.splice(at < 0 ? ordered.length : at, 0, it);
                return { ...d, items: ordered.map((i, n) => ({ ...i, sort_order: n })) };
              });
              void api.post(`/api/admin/backlog/${id}/move`, { before_id: beforeId }).catch((e) => { setError(msg(e, 'Could not move the item.')); void load(); });
            }} />
        ) : (
          <BoardView items={visible.filter((i) => !isShipped(i) && i.status !== 'wont_do' && i.kind !== 'epic')} hiddenDone={hiddenDone.length} onShowDone={() => showDone(true)} data={data} lanes={lanes} focusId={focusId}
            onContext={contextFor} onFocus={setFocusId} onQuickAdd={quickAdd} onDropFiles={upload}
            onDrop={(id, status, beforeId) => {
              const it = data.items.find((i) => i.id === id);
              if (!it) return;
              if (it.status !== status) handlers.update([id], { status });
              if (beforeId && beforeId !== id) {
                setData((d) => {
                  if (!d) return d;
                  const ordered = [...d.items].sort(comparator('rank')).filter((i) => i.id !== id);
                  ordered.splice(Math.max(0, ordered.findIndex((i) => i.id === beforeId)), 0, { ...it, status });
                  return { ...d, items: ordered.map((i, n) => ({ ...i, sort_order: n })) };
                });
                void api.post(`/api/admin/backlog/${id}/move`, { before_id: beforeId }).catch(() => load());
              }
            }} />
        )}
      </Card>

      <input ref={filePick} type="file" multiple accept={FILE_ACCEPT} className="hidden"
        onChange={(e) => { const id = attachTo.current; if (id) void upload(id, [...(e.target.files ?? [])]); e.target.value = ''; }} />
      {menu && <Menu x={menu.x} y={menu.y} entries={menu.entries} label={menu.label} onClose={() => setMenu(null)} />}
      {newItem && (
        <NewItemDialog open data={data} defaults={newItem} onClose={() => setNewItem(null)}
          onCreated={(item) => { setData((d) => (d ? { ...d, items: [...d.items, item] } : d)); notify(`${item.reference} added`); }} />
      )}
      {toast && (
        <div role="status" className="fixed bottom-6 right-6 z-40 flex items-center gap-3.5 rounded-xl bg-ink px-3.5 py-2.5 text-[13px] text-white shadow-pop">
          {toast.text}
          {toast.undo && <button type="button" onClick={toast.undo} className="border-0 bg-transparent p-0 font-bold text-[#AFC4EA]">Undo</button>}
          <button type="button" aria-label="Dismiss" onClick={() => setToast(null)} className="border-0 bg-transparent p-0 text-[#AEB8CC]"><X aria-hidden size={14} /></button>
        </div>
      )}
    </div>
  );
}

function Cover({ item, className }: { item: Item; className?: string }) {
  const url = useAdminImage(item.cover_id ? itemFile(item.id, item.cover_id) : null);
  if (!item.cover_id) return <span aria-hidden className={cx('block flex-none rounded-[5px] border border-line bg-panel', className)} />;
  return url ? <img src={url} alt="" className={cx('block flex-none rounded-[5px] border border-line object-cover', className)} />
    : <span aria-hidden className={cx('block flex-none animate-pulse rounded-[5px] border border-line bg-subtle', className)} />;
}

export function WherePill({ item }: { item: Item }) {
  if (!item.release_id) return <span className="text-[13px] text-muted">Not scheduled</span>;
  if (item.release_kind === 'hotfix') return <Pill tone="warn"><Zap aria-hidden size={12} /> {whereLabel(item)}</Pill>;
  return <Pill tone={item.release_status === 'shipped' ? 'ok' : item.release_status === 'in_progress' ? 'info' : 'purple'} dot>{item.release_name}</Pill>;
}

const pillBtn = 'cursor-pointer border-0 hover:ring-2 hover:ring-brand-ring';

function ListView(p: {
  groups: GroupOf[]; data: BacklogData; sort: Sort; setSort: (s: Sort) => void; selected: Set<string>; focusId: string | null; renaming: string | null;
  allSelected: boolean; onSelectAll: () => void; onToggle: (id: string, range: boolean) => void; onFocus: (id: string) => void;
  onContext: (e: ReactMouseEvent, i: Item) => void; onField: (el: HTMLElement, f: Field, i: Item) => void; onMore: (el: HTMLElement, i: Item) => void;
  onRename: (i: Item, title: string) => void; onCancelRename: () => void; onDropFiles: (id: string, files: File[]) => void;
  onQuickAdd: (title: string, base: GroupOf['add']) => void; canRank: boolean; onReorder: (id: string, beforeId: string) => void; onShowDone: () => void;
  childrenOf: ReadonlyMap<string, Item[]>; expanded: ReadonlySet<string>; onExpand: (id: string) => void; onEpic: (id: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; files: boolean } | null>(null);
  const sortHead = (s: Sort, label: string) => (
    <button type="button" onClick={() => p.setSort(p.sort === s ? 'rank' : s)} aria-sort={p.sort === s ? (s === 'updated' || s === 'created' ? 'descending' : 'ascending') : undefined}
      className={cx('inline-flex items-center gap-1 border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.05em]', p.sort === s ? 'text-brand' : 'text-muted hover:text-ink')}>
      {label}{p.sort === s ? (s === 'updated' ? <ArrowDown aria-hidden size={12} /> : <ArrowUp aria-hidden size={12} />) : <span aria-hidden className="opacity-50">↕</span>}
    </button>
  );
  const fileDrag = (e: DragEvent) => e.dataTransfer.types.includes('Files');
  const total = p.groups.reduce((n, g) => n + g.items.length, 0);
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse text-sm">
        <thead>
          <tr className="text-left">
            <th className="w-9 border-b border-line-soft px-2.5 py-2"><input type="checkbox" aria-label="Select all shown" checked={p.allSelected} onChange={p.onSelectAll} className="h-4 w-4 accent-brand" /></th>
            <th className="w-5 border-b border-line-soft" />
            <th className="w-[46%] min-w-[320px] border-b border-line-soft px-2.5 py-2 text-[11px] font-bold uppercase tracking-[0.05em] text-muted">Item</th>
            <th className="border-b border-line-soft px-2.5 py-2">{sortHead('priority', 'Priority')}</th>
            <th className="border-b border-line-soft px-2.5 py-2">{sortHead('status', 'Status')}</th>
            <th className="border-b border-line-soft px-2.5 py-2 text-[11px] font-bold uppercase tracking-[0.05em] text-muted">Ships in</th>
            <th className="border-b border-line-soft px-2.5 py-2">{sortHead('owner', 'Owner')}</th>
            <th className="border-b border-line-soft px-2.5 py-2">{sortHead('updated', 'Updated')}</th>
            <th className="w-9 border-b border-line-soft" />
          </tr>
        </thead>
        <tbody>
          {total === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted">Nothing matches. Clear a filter{p.groups.some((g) => g.hiddenDone) ? ', show done items' : ''}, or add an item.</td></tr>}
          {p.groups.map((g) => (
            <GroupRows key={g.key} g={g} {...p} anySelected={p.selected.size > 0} dragId={dragId} setDragId={setDragId} over={over} setOver={setOver} fileDrag={fileDrag} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({ g, data, selected, anySelected, focusId, renaming, onToggle, onFocus, onContext, onField, onMore, onRename, onCancelRename, onDropFiles, onQuickAdd, canRank, onReorder, onShowDone, childrenOf, expanded, onExpand, onEpic, dragId, setDragId, over, setOver, fileDrag }:
  Parameters<typeof ListView>[0] & { g: GroupOf; anySelected: boolean; dragId: string | null; setDragId: (id: string | null) => void; over: { id: string; files: boolean } | null;
    setOver: (o: { id: string; files: boolean } | null) => void; fileDrag: (e: DragEvent) => boolean }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  return (
    <>
      {g.label !== null && (
        <tr className="group/head">
          <td colSpan={9} className={cx('px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.05em]', g.hot ? 'bg-warn-bg text-warn-fg' : 'bg-panel text-muted')}>
            <span className="flex items-center gap-2">{g.label} · {g.items.length}
              {!!g.hiddenDone && (
                <button type="button" onClick={onShowDone} title="Show items that are Done but not shipped"
                  className="rounded-md border-0 bg-transparent px-1 text-[11px] font-semibold normal-case tracking-normal text-ok-fg hover:bg-white hover:underline">
                  + {g.hiddenDone} done
                </button>
              )}
              {g.add && !adding && <button type="button" aria-label="Quick add here" onClick={() => setAdding(true)} className="ml-auto flex h-6 items-center gap-1 rounded-md border-0 bg-transparent px-1.5 text-[11px] font-semibold normal-case tracking-normal text-muted opacity-0 hover:bg-white hover:text-brand focus:opacity-100 group-hover/head:opacity-100"><Plus aria-hidden size={13} /> Add</button>}
            </span>
          </td>
        </tr>
      )}
      {g.items.flatMap((i) => [i, ...(expanded.has(i.id) ? childrenOf.get(i.id) ?? [] : [])]).map((i) => {
        const sel = selected.has(i.id);
        const nested = !!i.parent_id && (childrenOf.get(i.parent_id) ?? []).includes(i);
        const level = levelOf(i);
        const kids = childrenOf.get(i.id) ?? [];
        const dropHere = over?.id === i.id;
        return (
          <tr key={i.id} data-item={i.id}
            onContextMenu={(e) => onContext(e, i)}
            onClick={() => onFocus(i.id)}
            onDragOver={(e) => { if (fileDrag(e) || (dragId && dragId !== i.id)) { e.preventDefault(); setOver({ id: i.id, files: fileDrag(e) }); } }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => {
              e.preventDefault(); setOver(null);
              if (e.dataTransfer.files.length) onDropFiles(i.id, [...e.dataTransfer.files]);
              else if (dragId && dragId !== i.id) onReorder(dragId, i.id);
              setDragId(null);
            }}
            className={cx('group border-t border-line-soft', sel ? 'bg-brand-tint' : 'hover:bg-row-hi', focusId === i.id && 'shadow-[inset_3px_0_0_rgb(var(--brand))]',
              dropHere && (over?.files ? 'bg-ok-bg shadow-[inset_0_0_0_1.5px_rgb(var(--ok))]' : 'shadow-[inset_0_2px_0_rgb(var(--brand))]'), dragId === i.id && 'opacity-50')}>
            <td className="px-2.5 py-1.5"><input type="checkbox" aria-label={`Select ${i.reference}`} checked={sel} onClick={(e) => { e.stopPropagation(); onToggle(i.id, e.shiftKey); }} onChange={() => {}} className={cx('h-4 w-4 accent-brand', !anySelected && 'opacity-0 focus:opacity-100 group-hover:opacity-100')} /></td>
            <td className="py-1.5">
              {canRank && <span draggable onDragStart={(e) => { setDragId(i.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', i.id); }} onDragEnd={() => { setDragId(null); setOver(null); }}
                title="Drag to rank" className="flex cursor-grab text-line-strong opacity-0 hover:text-muted group-hover:opacity-100"><GripVertical aria-hidden size={15} /></span>}
            </td>
            <td className={cx('px-2.5 py-1.5', nested && 'pl-10')}>
              <div className="flex items-center gap-2.5">
                {nested ? <CornerDownRight aria-hidden size={14} className="flex-none text-muted" />
                  : i.cover_id ? <Cover item={i} className="h-[30px] w-11" /> : <span aria-hidden className="w-11 flex-none" />}
                <div className="flex min-w-0 flex-col leading-tight">
                  {renaming === i.id ? (
                    <input autoFocus defaultValue={i.title} aria-label={`Rename ${i.reference}`} maxLength={160}
                      onKeyDown={(e) => { if (e.key === 'Enter') onRename(i, (e.target as HTMLInputElement).value); if (e.key === 'Escape') onCancelRename(); }}
                      onBlur={(e) => onRename(i, e.target.value)} className="h-8 w-[360px] rounded-lg border border-brand px-2 text-sm" />
                  ) : (
                    <Link to={`/admin/backlog/${i.id}`} onDoubleClick={(e) => { e.preventDefault(); onFocus(i.id); }} className="font-semibold text-ink no-underline hover:underline">
                      <span className="text-xs font-bold tabular-nums text-brand">{i.reference}</span>&nbsp; {i.title}
                    </Link>
                  )}
                  <span className="mt-0.5 flex flex-wrap items-center gap-2.5 text-xs text-muted">
                    {dropHere && over?.files ? <span className="font-semibold text-ok-fg">Drop to attach</span> : <>
                      <button type="button" onClick={(e) => onField(e.currentTarget, 'kind', i)} className={cx('border-0 bg-transparent p-0 text-xs font-semibold hover:underline', i.kind === 'bug_fix' ? 'text-bad' : i.kind === 'feature' ? 'text-brand' : i.kind === 'epic' ? 'text-purple-fg' : 'text-ink-2')}>{level === 'subtask' ? `Sub-task · ${KIND_LABEL[i.kind]}` : KIND_LABEL[i.kind]}</button>
                      {level === 'epic' && i.children_total > 0 && (
                        <span className="inline-flex items-center gap-1.5">{i.children_done}/{i.children_total} done<ProgressBar pct={Math.round((i.children_done / i.children_total) * 100)} height={4} className="w-16" /></span>
                      )}
                      {level === 'story' && i.parent && (
                        <button type="button" onClick={() => onEpic(i.parent_id!)} title={`Show only ${i.parent.title}`} className="inline-flex items-center gap-1 rounded border-0 bg-subtle px-1.5 text-xs text-ink-2 hover:text-brand">
                          <Layers aria-hidden size={11} />{i.parent.title}
                        </button>
                      )}
                      {level === 'subtask' && !nested && i.parent && <span className="inline-flex items-center gap-0.5"><CornerDownRight aria-hidden size={11} />{i.parent.reference}</span>}
                      {level === 'story' && i.children_total > 0 && (kids.length > 0 ? (
                        <button type="button" aria-expanded={expanded.has(i.id)} onClick={() => onExpand(i.id)} className="inline-flex items-center gap-0.5 rounded border-0 bg-transparent p-0 text-xs font-semibold text-ink-2 hover:text-brand">
                          {expanded.has(i.id) ? <ChevronDown aria-hidden size={12} /> : <ChevronRight aria-hidden size={12} />}Sub-tasks {i.children_done}/{i.children_total}
                        </button>
                      ) : <span>Sub-tasks {i.children_done}/{i.children_total}</span>)}
                      {i.origin_request_id && !i.owner_id && isNewFromFirm(i) && <span className="font-semibold text-brand">New from a firm</span>}
                      {i.area && <span>{AREA_LABEL[i.area] ?? i.area}</span>}
                      {i.requests.length > 0 && <span title={i.requests.map((r) => `${r.reference} · ${r.firm_name ?? r.firm_key}`).join('\n')}>{i.requests[0]!.reference}{i.requests.length > 1 ? ` +${i.requests.length - 1}` : ''}</span>}
                      {i.attachment_count > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip aria-hidden size={11} />{i.attachment_count}</span>}
                      {i.comment_count > 0 && <span className="inline-flex items-center gap-0.5"><MessageSquare aria-hidden size={11} />{i.comment_count}</span>}
                      {i.checklist_total > 0 && <span className="inline-flex items-center gap-0.5"><CheckSquare aria-hidden size={11} />{i.checklist_done}/{i.checklist_total}</span>}
                      {i.labels.map((l) => <span key={l} className="rounded bg-subtle px-1.5">{l}</span>)}
                    </>}
                  </span>
                </div>
              </div>
            </td>
            <td className="whitespace-nowrap px-2.5 py-1.5"><button type="button" onClick={(e) => onField(e.currentTarget, 'priority', i)} className={cx('rounded-pill bg-transparent p-0', pillBtn)}><Pill tone={PRIORITY_TONE[i.priority]}>{PRIORITY_LABEL[i.priority]}</Pill></button></td>
            <td className="whitespace-nowrap px-2.5 py-1.5"><button type="button" onClick={(e) => onField(e.currentTarget, 'status', i)} className={cx('rounded-pill bg-transparent p-0', pillBtn)}><Pill tone={STATUS_TONE[i.status]} dot>{STATUS_LABEL[i.status]}</Pill></button></td>
            <td className="whitespace-nowrap px-2.5 py-1.5">
              {isShipped(i) ? <WherePill item={i} /> : !canSchedule(i) ? (
                <span className="text-[13px] text-muted">{level === 'epic' ? epicReleases(i, data) : `With ${i.parent?.reference ?? 'its parent'}`}</span>
              ) : (
                <button type="button" onClick={(e) => onField(e.currentTarget, 'release', i)} className={cx('rounded-pill bg-transparent p-0', i.release_id ? pillBtn : 'border-0 text-[13px] text-muted hover:text-brand')}>
                  {i.release_id ? <WherePill item={i} /> : <><span className="group-hover:hidden">—</span><span className="hidden font-semibold group-hover:inline">Schedule</span></>}
                </button>
              )}
            </td>
            <td className="px-2.5 py-1.5">
              <button type="button" onClick={(e) => onField(e.currentTarget, 'owner', i)} title={i.owner_name ?? 'Unassigned'} aria-label={`Owner: ${i.owner_name ?? 'unassigned'}`}
                className="flex items-center gap-1.5 rounded-pill border-0 bg-transparent p-0 text-[13px] text-ink-2 hover:text-ink">
                {i.owner_name ? <><Avatar initials={initialsOf(i.owner_name)} size={24} tone="soft" /><span className="max-w-[90px] truncate">{i.owner_name.split(' ')[0]}</span></> : <span className="text-muted">—</span>}
              </button>
            </td>
            <td className="whitespace-nowrap px-2.5 py-1.5 text-[13px] text-muted">{relative(i.updated_at)}</td>
            <td className="py-1.5 pr-2"><button type="button" aria-label={`More actions for ${i.reference}`} onClick={(e) => { e.stopPropagation(); onMore(e.currentTarget, i); }} className="flex h-7 w-7 items-center justify-center rounded-lg border-0 bg-transparent text-ink-2 opacity-0 hover:bg-subtle focus:opacity-100 group-hover:opacity-100"><MoreHorizontal aria-hidden size={16} /></button></td>
          </tr>
        );
      })}
      {g.add && adding && (
        <tr className="border-t border-line-soft">
          <td colSpan={9} className="px-2.5 py-1">
            {adding ? (
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} aria-label="New item title" placeholder='Title. Start with "bug:" for a bug.'
                onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) { onQuickAdd(title, g.add); setTitle(''); } if (e.key === 'Escape') { setAdding(false); setTitle(''); } }}
                onBlur={() => { if (!title.trim()) setAdding(false); }} className="h-8 w-full max-w-[520px] rounded-lg border border-brand px-2 text-sm" />
            ) : (
              <button type="button" onClick={() => setAdding(true)} className="min-h-7 border-0 bg-transparent px-1 text-[13px] font-medium text-muted hover:text-brand">+ Quick add{g.label ? ' here' : ''}</button>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Where an epic's items ship: "1.7 · Next hotfix", or a dash. */
function epicReleases(epic: Item, data: BacklogData) {
  const names = [...new Set(data.items.filter((i) => i.parent_id === epic.id && i.release_id && !i.archived_at).map((i) => whereLabel(i)))];
  return names.length ? names.join(' · ') : '—';
}

function relative(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return mins <= 1 ? 'Just now' : `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'Yesterday' : d < 7 ? `${d}d ago` : fmtDate(iso);
}

function BoardView({ items, hiddenDone, onShowDone, data, lanes, focusId, onContext, onFocus, onDrop, onQuickAdd, onDropFiles }: {
  items: Item[]; hiddenDone: number; onShowDone: () => void; data: BacklogData; lanes: 'none' | 'owner' | 'release' | 'priority'; focusId: string | null;
  onContext: (e: ReactMouseEvent, i: Item) => void; onFocus: (id: string) => void; onDrop: (id: string, status: ItemStatus, beforeId?: string) => void;
  onQuickAdd: (title: string, base: GroupOf['add']) => void; onDropFiles: (id: string, files: File[]) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [hover, setHover] = useState<{ col: string; before?: string } | null>(null);
  const rows = lanes === 'none' ? [{ key: 'all', label: null as ReactNode, items, add: {} as GroupOf['add'] }] : grouped(items, lanes, data, new Map(data.items.map((i) => [i.id, i]))).filter((g) => g.items.length);
  return (
    <div className="flex flex-col gap-4 p-3">
      {rows.map((row) => (
        <section key={row.key} aria-label={typeof row.label === 'string' ? row.label : 'Items'}>
          {row.label !== null && <h3 className="m-0 mb-2 text-[13px] font-bold">{row.label} <span className="font-semibold text-muted">· {row.items.length}</span></h3>}
          <div className="flex items-start gap-2.5 overflow-x-auto">
            {BOARD_COLUMNS.map((s) => {
              const col = row.items.filter((i) => i.status === s).sort(comparator('rank'));
              const colKey = `${row.key}:${s}`;
              return (
                <div key={s} className={cx('flex min-w-[220px] flex-1 flex-col gap-2 rounded-card bg-subtle p-2.5', hover?.col === colKey && !hover.before && 'ring-2 ring-brand-ring')}
                  onDragOver={(e) => { if (dragId) { e.preventDefault(); if (!hover || hover.col !== colKey) setHover({ col: colKey }); } }}
                  onDrop={(e) => { e.preventDefault(); if (dragId) onDrop(dragId, s, hover?.before); setDragId(null); setHover(null); }}>
                  <div className="flex items-center gap-2 px-1 text-[13px] font-bold">
                    <Pill tone={STATUS_TONE[s]} dot>{STATUS_LABEL[s]}</Pill><span className="font-semibold text-muted">{col.length}</span>
                    {s === 'done' && (hiddenDone > 0 && lanes === 'none'
                      ? <button type="button" onClick={onShowDone} className="ml-auto border-0 bg-transparent p-0 text-[11px] font-semibold text-brand hover:underline">Show {hiddenDone} hidden</button>
                      : <span className="ml-auto text-[11px] font-medium text-muted">not yet shipped</span>)}
                  </div>
                  {col.map((i) => (
                    <div key={i.id}>
                      {hover?.col === colKey && hover.before === i.id && <div className="mb-2 h-14 rounded-xl border-[1.5px] border-dashed border-brand-ring bg-white/60" />}
                      <BoardCard item={i} focused={focusId === i.id} dragging={dragId === i.id}
                        onDragStart={() => setDragId(i.id)} onDragEnd={() => { setDragId(null); setHover(null); }}
                        onDragOver={(e) => { if (dragId && dragId !== i.id) { e.preventDefault(); e.stopPropagation(); setHover({ col: colKey, before: i.id }); } else if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
                        onDropFiles={(files) => onDropFiles(i.id, files)}
                        onContext={(e) => onContext(e, i)} onClick={() => onFocus(i.id)} />
                    </div>
                  ))}
                  <ColumnAdd onAdd={(t) => onQuickAdd(t, { ...row.add, status: s })} />
                </div>
              );
            })}
          </div>
        </section>
      ))}
      <p className="m-0 text-xs text-muted">Shipped and Won’t do items are hidden here; switch to the list to see them. Drop an image on a card to attach it.</p>
    </div>
  );
}

function BoardCard({ item, focused, dragging, onDragStart, onDragEnd, onDragOver, onDropFiles, onContext, onClick }: {
  item: Item; focused: boolean; dragging: boolean; onDragStart: () => void; onDragEnd: () => void; onDragOver: (e: DragEvent) => void;
  onDropFiles: (files: File[]) => void; onContext: (e: ReactMouseEvent) => void; onClick: () => void;
}) {
  const navigate = useNavigate();
  return (
    <div data-item={item.id} draggable role="button" tabIndex={0}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', item.id); onDragStart(); }}
      onDragEnd={onDragEnd} onDragOver={onDragOver}
      onDrop={(e) => { if (e.dataTransfer.files.length) { e.preventDefault(); e.stopPropagation(); onDropFiles([...e.dataTransfer.files]); } }}
      onContextMenu={onContext} onClick={onClick} onDoubleClick={() => navigate(`/admin/backlog/${item.id}`)}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/backlog/${item.id}`); }}
      className={cx('flex cursor-grab flex-col gap-1.5 rounded-xl border bg-white p-2.5 shadow-card', focused ? 'border-brand' : 'border-line', dragging && 'opacity-40')}>
      {item.cover_id && <Cover item={item} className="h-[84px] w-full" />}
      <div className="flex items-center gap-1.5">
        <Link to={`/admin/backlog/${item.id}`} className="text-[11px] font-bold text-brand no-underline">{item.reference}</Link>
        <Pill tone={KIND_TONE[item.kind]} className="!px-2 !py-0 !text-[11px]">{KIND_LABEL[item.kind]}</Pill>
        {item.release_id && (item.release_kind === 'hotfix'
          ? <Pill tone="warn" className="ml-auto !px-2 !py-0 !text-[11px]"><Zap aria-hidden size={10} /> Next hotfix</Pill>
          : <Pill tone={item.release_status === 'in_progress' ? 'info' : 'purple'} dot className="ml-auto !px-2 !py-0 !text-[11px]">{item.release_name}</Pill>)}
      </div>
      <div className="text-[13px] font-semibold leading-snug">{item.title}</div>
      <div className="flex items-center gap-1.5 text-[11px] text-muted">
        <Pill tone={PRIORITY_TONE[item.priority]} className="!px-2 !py-0 !text-[11px]">{PRIORITY_LABEL[item.priority]}</Pill>
        {item.attachment_count > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip aria-hidden size={10} />{item.attachment_count}</span>}
        {item.comment_count > 0 && <span className="inline-flex items-center gap-0.5"><MessageSquare aria-hidden size={10} />{item.comment_count}</span>}
        {item.checklist_total > 0 && <span>☑ {item.checklist_done}/{item.checklist_total}</span>}
        {item.requests.length > 0 && <span>FB {item.requests.length}</span>}
        <span className="ml-auto">{item.owner_name ? <Avatar initials={initialsOf(item.owner_name)} size={22} tone="soft" /> : <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full border border-dashed border-line-strong text-[10px]">?</span>}</span>
      </div>
    </div>
  );
}

function ColumnAdd({ onAdd }: { onAdd: (title: string) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="min-h-8 rounded-lg border-0 bg-transparent text-left text-[13px] font-medium text-muted hover:bg-white hover:text-brand">+ Add</button>;
  return (
    <div className="flex flex-col gap-1 rounded-xl border-[1.5px] border-brand bg-white p-2.5">
      <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} aria-label="New item title" placeholder="Title"
        onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) { onAdd(title); setTitle(''); } if (e.key === 'Escape') { setOpen(false); setTitle(''); } }}
        onBlur={() => { if (!title.trim()) setOpen(false); }} className="border-0 p-0 text-[13px] font-semibold outline-none" />
      <span className="text-[11px] text-muted">Start with “bug:” for a bug</span>
    </div>
  );
}
